import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import type { DocumentRoot } from "./collab-document.tsx";
import { CurriculaIndex } from "./curricula-index.tsx";
import { AppErrorBoundary } from "./error-pages.tsx";
import { AuthGate } from "./unauthenticated-page.tsx";

// Everything below is reached only after the reviewer picks something off the index, so it loads as
// a separate chunk instead of the entry bundle. `DocumentView` alone pulls in CodeMirror, the collab
// surface and the assistant-ui chat stack — the bulk of the app's JS — none of which the index route
// (the first thing every session loads) needs.
const DocumentView = lazy(() => import("./routes.tsx").then((m) => ({ default: m.DocumentView })));
const LanguagePacksIndex = lazy(() =>
  import("./language-packs-page.tsx").then((m) => ({ default: m.LanguagePacksIndex })),
);
const PersonasPage = lazy(() =>
  import("./personas-page.tsx").then((m) => ({ default: m.PersonasPage })),
);
const ReferenceGuidesIndex = lazy(() =>
  import("./reference-guides-page.tsx").then((m) => ({ default: m.ReferenceGuidesIndex })),
);
const EnrichmentPage = lazy(() =>
  import("./enrichment-page.tsx").then((m) => ({ default: m.EnrichmentPage })),
);

// The four sub-routes every document root gets, one `DocumentView` element parameterized by `root` —
// curricula, language-packs and reference-guides all wire up the same shape below. Same element for
// all four paths within a root so switching between them (e.g. plain view -> edit mode) never remounts
// `DocumentView`/`CollabDocumentSurface`, tearing down the live WS session — only the params change.
//
// - `/:id` — the plain, read-only-by-default document view.
// - `/:id/edit/:draftId` (U13 Part C) — entering edit mode (forking a draft, or selecting any draft's
//   diff) rewrites the URL to carry the draftId. Read-only-kind reference guides never get a draft to
//   land here with, but the route itself is root-agnostic.
// - `/:id/compare/:draftId` (R5/Q4) — the durable read-only compare surface: a diff of ANY draft
//   (any owner/lifecycle, resolved server-side via `DraftStore.get`) against the main document,
//   rendered inside the document-view layout (`GET /api/drafts/:draftId/compare` — U8: root-agnostic,
//   since a draft's own `docId` resolves the document, not this route's URL). Reachable even for an
//   idle-hidden or terminal draft the rail no longer shows.
// - `/:id/history/:version` — the read-only history surface: a past version's diff (base vs applied
//   result), rendered with the same merge view compare uses. `collab-document.tsx` detects the
//   `/history/` path and renders the selected version's reconstructed diff.
//
// `:id` is a single, `encodeURIComponent`-ed path segment (see each root's index page) — a
// `LanguagePackPath`/`ReferenceGuidePath` contains its own `/` (e.g. `L2_QMS/personas.md`) that a
// plain segment would otherwise truncate; react-router decodes it straight back for `useParams()`. A
// curriculum id never contains a further `/`, so it passes through unchanged.
const documentRoutes = (root: DocumentRoot) => (
  <>
    <Route path={`/${root}/:id`} element={<DocumentView root={root} />} />
    <Route path={`/${root}/:id/edit/:draftId`} element={<DocumentView root={root} />} />
    <Route path={`/${root}/:id/compare/:draftId`} element={<DocumentView root={root} />} />
    <Route path={`/${root}/:id/history/:version`} element={<DocumentView root={root} />} />
  </>
);

// The review routes. The curriculum index is the entry; the document view serves a curriculum.
// The rubric/glossary are no longer viewable documents (ADR-0055) — they are static files behind
// the RubricCatalog port. The rule-metrics route is retired along with the rubric-refinement loop.
//
// The whole route tree sits inside `AppErrorBoundary`. Any render error that escapes a route
// used to unmount the app and leave a white page (React's default when no boundary exists) — the
// `applyChangeSetJson` RangeError in `routes.tsx`'s `getRule` comment is a production instance of it.
// INSIDE `BrowserRouter`, so the fallback can still render a `<Link>`; a route that names its own
// refusal (a malformed document id → `NotFoundPage`) never reaches this — that is a 404, not a fault.
export const App = () => (
  <BrowserRouter>
    <AppErrorBoundary>
      <AuthGate>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/curricula" element={<CurriculaIndex />} />
            {/* The enrichment_pipeline launch surface — document-less: a push is keyed by an
              exercise-version label plus live CSS ids, none of which are fields of a studio
              document, so it is its own top-level page rather than a tab on a document. (The
              l2_unit_plan launch surface this originally sat alongside, `/generate`, was retired
              by CON-1316/U5b+U6 in favour of a "New Curriculum" modal on `CurriculaIndex` itself.) */}
            <Route path="/enrichment" element={<EnrichmentPage />} />
            {documentRoutes("curricula")}
            {/* The human review/edit surface for language-pack docs (ADR-0097): a list of the six
          document types per language (`LanguagePacksIndex`), and — since U8 — the SAME CodeMirror
          collab surface curricula use for one doc at a time. Sub-routes: see `documentRoutes` above. */}
            <Route path="/language-packs" element={<LanguagePacksIndex />} />
            {/* ADR-0118: one pack's personas — registry entries plus identity-kit images. A literal
          `personas` segment, which no `encodeURIComponent`-ed document id can equal. */}
            <Route path="/language-packs/personas/:pack" element={<PersonasPage />} />
            {documentRoutes("language-packs")}
            {/* The human review/edit surface for reference-guide docs (ADR-0099/ADR-0108): the
          curriculum reference + authoring guides, mirroring the language-pack routes above one
          document class over. Three of the five kinds (`READ_ONLY_KINDS`) render read-only on the
          collab surface (`collab-document.tsx`'s `readOnly` prop) rather than being routed
          differently here. Sub-routes: see `documentRoutes` above. */}
            <Route path="/reference-guides" element={<ReferenceGuidesIndex />} />
            {documentRoutes("reference-guides")}
            {/* The Agent Sessions tab + its raw-transcript routes are retired (ADR-0064): the Anthropic
          platform console is the transcript view; the chat panel reads a viewer's own conversations
          live from the managed platform. */}
            <Route path="*" element={<Navigate to="/curricula" replace />} />
          </Routes>
        </Suspense>
      </AuthGate>
    </AppErrorBoundary>
  </BrowserRouter>
);
